/**
 * AtlasTab — iter86.30 · Canon → Atlas (taxonomy-health explorer).
 *
 * The read-only map of the whole ladder: TCL commitments → Values →
 * Standards → Signals, with Obligations alongside. Pick any node and see
 * what expresses it, what it governs, and where the open work is
 * (pending classifications, non-active signals). Decisions never happen
 * here — deep-links hand off to Stewardship.
 */
import { useEffect, useMemo, useState } from "react";
import { Map as MapIcon, Layers, Scale, Link2, ArrowUpRight, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

function Pill({ cls, children, testId }) {
    return <span data-testid={testId} className={`pill text-[9px] font-bold uppercase border ${cls}`}>{children}</span>;
}

const LADDER_BLURB = "TCL Commitments ↑ are expressed by crew-level Standards, grouped into Values; game-specific Standards stay product-local. Standards are proven by Signals (CI checks & gates). Obligations are imposed from outside and sit beside Values.";

export default function AtlasTab({ onNavigate }) {
    const [values, setValues] = useState(null);
    const [mapping, setMapping] = useState(null);
    const [obligations, setObligations] = useState(null);
    const [error, setError] = useState(null);
    const [sel, setSel] = useState(null); // {kind: 'value'|'standard'|'obligation', id}

    useEffect(() => {
        Promise.all([api.releaseOpsValuesRegistry(), api.tclMapping(), api.releaseOpsObligationsRegistry()])
            .then(([v, m, o]) => { setValues(v); setMapping(m); setObligations(o); })
            .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load atlas"));
    }, []);

    const catalog = useMemo(() => mapping?.standards_catalog || [], [mapping?.standards_catalog]);
    const valueGroups = useMemo(() => values?.value_groups || {}, [values?.value_groups]);
    const oblGroups = obligations?.obligation_groups || {};

    const standardsByValue = useMemo(() => {
        const out = {};
        for (const s of catalog) {
            const key = s.value_group || "—";
            (out[key] = out[key] || []).push(s);
        }
        return out;
    }, [catalog]);

    const signalStatus = useMemo(() => {
        const out = {};
        for (const node of Object.values(valueGroups)) {
            for (const c of node.children || []) out[c.name] = c.status;
        }
        for (const c of values?.values_engine?.crew_level || []) out[c.name] = out[c.name] || c.status;
        return out;
    }, [valueGroups, values]);

    if (error) return <div className="text-xs text-rose-300" data-testid="atlas-error">{error}</div>;
    if (!values || !mapping || !obligations) {
        return <div className="text-xs text-zinc-500" data-testid="atlas-loading">Loading atlas…</div>;
    }

    const pending = values.pending_count || 0;

    const SignalChip = ({ name }) => {
        const st = signalStatus[name];
        const bad = st && st !== "active";
        return (
            <span className={`pill text-[9px] border ${bad ? "bg-amber-900/40 text-amber-200 border-amber-600" : "bg-zinc-900 text-zinc-400 border-zinc-700"}`} data-testid={`atlas-signal-${name}`}>
                {name}{bad ? ` · ${st}` : ""}
            </span>
        );
    };

    const StandardBlock = ({ s }) => (
        <div className="border border-zinc-800 rounded p-2.5 bg-black/30" data-testid={`atlas-standard-${s.standard_id.replace(/[^a-z0-9-]/gi, "_")}`}>
            <div className="flex items-center gap-2 flex-wrap mb-1">
                <button type="button" onClick={() => setSel({ kind: "standard", id: s.standard_id })}
                    className="text-xs font-bold text-fuchsia-200 hover:underline" data-testid={`atlas-standard-link-${s.standard_id.replace(/[^a-z0-9-]/gi, "_")}`}>
                    {s.standard_id}
                </button>
                {s.expresses_tcl?.length > 0 && <span className="text-[10px] text-zinc-500">↑ {s.expresses_tcl.join(", ")}</span>}
                {s.status !== "active" && <Pill cls="bg-amber-900/40 text-amber-200 border-amber-600">{s.status}</Pill>}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
                {(s.signals || []).map((sig) => <SignalChip key={sig} name={sig} />)}
            </div>
        </div>
    );

    const detail = (() => {
        if (!sel) return null;
        if (sel.kind === "value") {
            const node = valueGroups[sel.id];
            const stds = standardsByValue[sel.id] || [];
            if (!node) return null;
            return (
                <div className="space-y-3" data-testid="atlas-detail-value">
                    <p className="text-xs text-zinc-300 italic">“{node.statement}”</p>
                    <p className="text-[10px] text-zinc-500 uppercase font-bold">{stds.length} standard(s) · {stds.reduce((n, s) => n + (s.signals?.length || 0), 0)} signal(s)</p>
                    <div className="space-y-2">{stds.map((s) => <StandardBlock key={s.standard_id} s={s} />)}</div>
                </div>
            );
        }
        if (sel.kind === "standard") {
            const s = catalog.find((x) => x.standard_id === sel.id);
            if (!s) return null;
            return (
                <div className="space-y-3" data-testid="atlas-detail-standard">
                    <div className="flex items-center gap-2 flex-wrap text-[11px]">
                        {s.value_group
                            ? <button type="button" className="text-cyan-300 hover:underline" onClick={() => setSel({ kind: "value", id: s.value_group })}>↑ {s.value_group}</button>
                            : <span className="text-zinc-500">game-specific — does not ladder up</span>}
                        {s.expresses_tcl?.length > 0 && <span className="text-zinc-500">↑↑ {s.expresses_tcl.join(", ")}</span>}
                    </div>
                    <p className="text-xs text-zinc-300">{s.statement}</p>
                    <div className="flex items-center gap-1.5 flex-wrap">{(s.signals || []).map((sig) => <SignalChip key={sig} name={sig} />)}</div>
                </div>
            );
        }
        const g = oblGroups[sel.id];
        if (!g) return null;
        return (
            <div className="space-y-3" data-testid="atlas-detail-obligation">
                <p className="text-xs text-zinc-300 italic">{g.statement}</p>
                <p className="text-[10px] text-zinc-500 uppercase font-bold">{g.active_children} active obligation(s) · posture {g.posture}</p>
                <div className="space-y-1.5">
                    {(g.children || []).map((c) => (
                        <div key={c.name} className="border border-zinc-800 rounded p-2 text-[11px] flex items-center gap-2 flex-wrap bg-black/30">
                            <span className="font-bold text-white">{c.name}</span>
                            {c.compliance_status && <Pill cls={c.compliance_status === "compliant" ? "bg-emerald-900/40 text-emerald-200 border-emerald-700" : "bg-amber-900/40 text-amber-200 border-amber-600"}>{c.compliance_status}</Pill>}
                        </div>
                    ))}
                </div>
            </div>
        );
    })();

    const NodeBtn = ({ kind, id, label, count, warn }) => (
        <button
            type="button"
            onClick={() => setSel({ kind, id })}
            className={`w-full text-left border rounded px-2.5 py-1.5 text-[11px] flex items-center gap-2 transition-colors ${sel?.kind === kind && sel?.id === id ? "border-fuchsia-600 bg-fuchsia-950/30 text-white" : "border-zinc-800 bg-black/30 text-zinc-300 hover:border-zinc-600"}`}
            data-testid={`atlas-node-${kind}-${id.replace(/[^a-z0-9-]/gi, "_")}`}
        >
            <span className="truncate">{label}</span>
            <span className="ml-auto text-[9px] text-zinc-500">{count}</span>
            {warn > 0 && <span className="text-[9px] text-amber-300">⚠ {warn}</span>}
        </button>
    );

    return (
        <div className="space-y-4" data-testid="atlas-tab">
            <div className="brutal-card p-4 border-2 border-fuchsia-900/40 bg-fuchsia-950/10">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <MapIcon className="w-4 h-4 text-fuchsia-300" />
                    <h2 className="font-display font-extrabold text-lg uppercase tracking-wide text-white">Atlas — The Canon Map</h2>
                    <Pill cls="bg-fuchsia-900/40 text-fuchsia-200 border-fuchsia-700">Read-only</Pill>
                </div>
                <p className="text-xs text-zinc-400">{LADDER_BLURB}</p>
                {pending > 0 && (
                    <div className="flex items-center gap-2 mt-2 text-[11px] text-amber-200" data-testid="atlas-pending-banner">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                        {pending} entries pending classification —
                        <button type="button" className="underline hover:text-white" onClick={() => onNavigate && onNavigate("stewardship")} data-testid="atlas-goto-stewardship">
                            decide in Stewardship <ArrowUpRight className="w-3 h-3 inline" />
                        </button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="space-y-4">
                    <div className="brutal-card p-3">
                        <h3 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><Layers className="w-3 h-3" /> Values · {Object.keys(valueGroups).filter((s) => valueGroups[s]?.statement).length}</h3>
                        <div className="space-y-1.5">
                            {Object.keys(valueGroups).filter((s) => valueGroups[s]?.statement).map((slug) => (
                                <NodeBtn key={slug} kind="value" id={slug} label={slug}
                                    count={`${(standardsByValue[slug] || []).length} std`}
                                    warn={(valueGroups[slug].children || []).filter((c) => c.status !== "active").length} />
                            ))}
                        </div>
                    </div>
                    <div className="brutal-card p-3">
                        <h3 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><Link2 className="w-3 h-3" /> Standards · {catalog.length}</h3>
                        <div className="space-y-1.5">
                            {catalog.map((s) => (
                                <NodeBtn key={s.standard_id} kind="standard" id={s.standard_id}
                                    label={s.standard_id.replace(/^(standards|policies)\//, "")}
                                    count={`${(s.signals || []).length} sig`}
                                    warn={s.status !== "active" ? 1 : 0} />
                            ))}
                        </div>
                    </div>
                    <div className="brutal-card p-3">
                        <h3 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><Scale className="w-3 h-3" /> Obligations · {Object.keys(oblGroups).length}</h3>
                        <div className="space-y-1.5">
                            {Object.entries(oblGroups).map(([slug, g]) => (
                                <NodeBtn key={slug} kind="obligation" id={slug}
                                    label={slug.replace(/^obligations\//, "")}
                                    count={`${g.active_children} active`}
                                    warn={g.posture && g.posture !== "ok" ? 1 : 0} />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <div className="brutal-card p-4 min-h-[200px]" data-testid="atlas-detail-panel">
                        {sel ? (
                            <>
                                <div className="flex items-center gap-2 flex-wrap mb-3">
                                    <Pill cls="bg-zinc-800 text-zinc-300 border-zinc-600">{sel.kind}</Pill>
                                    <code className="text-sm font-bold text-white">{sel.id}</code>
                                    <button type="button" className="ml-auto text-[10px] text-zinc-500 underline hover:text-white"
                                        onClick={() => onNavigate && onNavigate(sel.kind === "obligation" ? "obligations" : "stewardship")}
                                        data-testid="atlas-detail-handoff">
                                        {sel.kind === "obligation" ? "open Obligations" : "decide in Stewardship"} <ArrowUpRight className="w-3 h-3 inline" />
                                    </button>
                                </div>
                                {detail}
                            </>
                        ) : (
                            <p className="text-xs text-zinc-500" data-testid="atlas-empty-hint">
                                Pick a Value, Standard, or Obligation group on the left to see its full ladder — what it expresses upstream, what proves it downstream, and where the open work is.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
